import { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("admin_token"));

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setIsLoading(false);
    }
  }, []);

  const verifyToken = async () => {
    try {
      const response = await axios.post(
        `${API}/auth/verify`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.data.valid) {
        setAdmin({ username: response.data.username });
      } else {
        logout();
      }
    } catch (error) {
      logout();
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (username, password) => {
    const response = await axios.post(`${API}/auth/login`, {
      username,
      password,
    });
    const { access_token } = response.data;
    localStorage.setItem("admin_token", access_token);
    setToken(access_token);
    setAdmin({ username });
    return response.data;
  };

  const register = async (username, password, adminSecret) => {
    const response = await axios.post(`${API}/auth/register`, {
      username,
      password,
      admin_secret: adminSecret,
    });
    const { access_token } = response.data;
    localStorage.setItem("admin_token", access_token);
    setToken(access_token);
    setAdmin({ username });
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    setToken(null);
    setAdmin(null);
  };

  const getAuthHeader = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider
      value={{
        admin,
        isLoading,
        isAuthenticated: !!admin,
        login,
        register,
        logout,
        getAuthHeader,
        token,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
